import { useAdminConfig, useCurrentUser, useTenantSettings } from '@centecomm/portal-service-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoadStatus, State } from '../../../Types';
import { SettingsActions } from '../actions';
import { UserTypeAgnosticSettings } from '../Types/UserTypeAgnosticSettings';

export type SettingsHookData<T extends UserTypeAgnosticSettings> = {
    /** Whether the settings have been loaded. */
    isLoaded: boolean;
    /** Whether the settings are currently being loaded. */
    isLoading: boolean;
    /** Whether there was an error loading the settings. */
    hasError: boolean;
    /** The settings data. */
    settings?: T;
};

/**
 * Hook to get the full settings from the state.
 * This hook will load the settings if they are not already loaded, and will fill in any gaps in the tenant data.
 * It takes a generic type to specify the type of settings to load if you need specificity (under OO or Tenant contexts).
 * @returns The settings data.
 */
function useSettingsFromState<T extends UserTypeAgnosticSettings>(): SettingsHookData<T> {
    const { isTenant } = useCurrentUser();
    const { getAllSettings } = useAdminConfig();
    const { getTenantSettings } = useTenantSettings();
    const dispatch = useDispatch();

    const rawSettingsFromState = useSelector((state: State) => state.settings.data);
    const loadStatus = useSelector((state: State) => state.settings.loadStatus);

    /**
     * Raw settings combined with custom data to fill in gaps in the tenant data.
     */
    const settings: T | undefined = React.useMemo(() => {
        if (!rawSettingsFromState) return undefined;

        return {
            ...rawSettingsFromState,
            insurance: isTenant
                ? {
                      visible: rawSettingsFromState.insurance?.visible,
                      daysToNotify: [3, 7, 14, 30],
                      coiAcceptanceNotification: {},
                  }
                : rawSettingsFromState.insurance,
            leasing: isTenant
                ? {
                      visible: rawSettingsFromState.leasing?.visible,
                      notification: {
                          enabled: true,
                          inactivityDays: 30,
                      },
                  }
                : rawSettingsFromState.leasing,
        } as unknown as T;
    }, [rawSettingsFromState]);

    React.useEffect(() => {
        if (loadStatus === LoadStatus.INITIAL_STATE) {
            dispatch({ type: SettingsActions.LOAD_SETTINGS });

            const settingsPromise = !isTenant ? getAllSettings() : getTenantSettings();

            settingsPromise
                .then((data: unknown) => {
                    dispatch({
                        type: SettingsActions.SET_SETTINGS,
                        payload: data as T,
                    });
                })
                .catch((error) => {
                    console.error(error);
                    dispatch({
                        type: SettingsActions.SET_ERROR,
                        payload: {
                            errorMessage: error?.code || 'We were not able to load the application settings.',
                        },
                    });
                });
        }
    }, [loadStatus, isTenant]);

    return {
        isLoaded: loadStatus === LoadStatus.LOADED,
        isLoading: loadStatus === LoadStatus.PENDING,
        hasError: loadStatus === LoadStatus.ERROR,
        settings,
    };
}

export default useSettingsFromState;
